/**
 * Investor onboarding field schema.
 *
 * Drives the multi-section onboarding form (InvestorOnboardingPage) generically:
 * sections → boxes → fields. Mandatory fields (`required: true`) are always visible;
 * everything else is `advanced` and hidden behind the box's "Advanced" toggle.
 *
 * Field jobs (see docs / spec):
 *   tag "match" → feeds the listing-matching engine (maps onto a listing attribute)
 *   tag "calc"  → underwriting / computed capacity inputs & outputs
 *   tag "crm"   → internal workflow (engagement, alerts) — not property fit
 *
 * `type` values handled by InvestorOnboardingField:
 *   text | textarea | email | tel | date
 *   currency (raw number via CurrencyInput) | number | percent
 *   select (single <select>) | multiselect (checkbox chips)
 *   boroughs (checkbox chips from BOROUGHS) | neighborhoods (NeighborhoodSelect multi)
 *   users (team-member <select>, options injected at render)
 *   computed (read-only; derived server-side at match time)
 *
 * Keep field `key`s stable — they map 1:1 onto DB columns in the backend tables.
 */

export const BOROUGHS = ["Manhattan", "Brooklyn", "Queens", "Bronx", "Staten Island", "NJ", "Other"];

// Shared option lists reused across boxes.
const FEATURE_OPTIONS = ["Elevator", "Doorman", "Renovated", "W/D", "Central air", "Dishwasher"];

export const ONBOARDING_SCHEMA = [
  // ───────────────────────────── SECTION 1 ─────────────────────────────
  {
    id: "profile",
    label: "Profile & Finances",
    title: "Client Personal & Financial Profile",
    subtitle:
      "Contact identity plus the underwriting picture. Feeds the computed capacity that the search filters use.",
    boxes: [
      {
        title: "Household & Contact",
        tag: "crm",
        fields: [
          { key: "household_name", label: "Household name", type: "text", required: true },
          { key: "emails", label: "Emails", type: "text", required: true, placeholder: "Comma-separated" },
          { key: "cell_phone", label: "Cell phone", type: "tel", required: true },
          { key: "members", label: "Household members", type: "members", required: true },
        ],
      },
      {
        title: "Capital & Budget",
        tag: "calc",
        fields: [
          { key: "available_equity", label: "Available equity / down payment", type: "currency", required: true },
          { key: "total_oop_capacity", label: "Total out-of-pocket capacity", type: "currency", required: true, help: "Equity + closing costs + reserves the client can deploy" },
          { key: "additional_liquid_assets", label: "Additional liquid assets", type: "currency", required: true, help: "Cash, securities, investment & bank accounts" },
          { key: "illiquid_assets", label: "Illiquid assets", type: "currency", help: "Real estate, business interests, retirement, insurance" },
          { key: "large_liabilities", label: "Large liabilities", type: "currency", help: "Loans, mortgages, cards, alimony, business debt (for DTI)" },
          { key: "existing_re_debt", label: "Existing real estate debt", type: "textarea", help: "Loan balance, payment, rate, maturity on other properties" },
          { key: "max_monthly_payment_comfort", label: "Max monthly payment comfort", type: "currency", help: "All-in comfort; see also carrying cost in Search" },
        ],
      },
      {
        title: "Credit, Citizenship & Residency",
        tag: "calc",
        fields: [
          { key: "credit_tier", label: "Credit score / tier", type: "select", required: true, options: ["Excellent", "Good", "Fair", "Poor", "Unknown"] },
          { key: "us_credit_history", label: "US credit history", type: "select", required: true, options: ["Strong", "Limited", "None", "Unknown"] },
          { key: "us_person", label: "US person", type: "select", required: true, options: ["Yes", "No", "Not sure"], help: "For financing / tax / lender treatment only" },
          { key: "residency_country", label: "Residency — country", type: "country", required: true },
          { key: "residency_state", label: "Residency — state / province", type: "usstate", dependsOn: "residency_country" },
          { key: "residency_city", label: "Residency — city", type: "text" },
          { key: "citizenship", label: "Citizenship / passport", type: "country", help: "Sensitive — kept out of property recommendation logic" },
          { key: "other_nationality", label: "Other nationality", type: "country" },
          { key: "visa_status", label: "Visa / immigration status", type: "select", options: ["Permanent resident", "Visa holder", "Foreign national", "Not applicable", "Unknown"] },
        ],
      },
      {
        title: "Financing Structure",
        tag: "calc",
        fields: [
          { key: "financing_required", label: "Financing required", type: "select", required: true, options: ["Cash", "Financing", "Not sure"] },
          { key: "target_ltv", label: "Target financing %", type: "percent" },
          { key: "max_ltv", label: "Max financing %", type: "percent" },
          { key: "financing_type", label: "Financing type", type: "multiselect", options: ["Mortgage", "Credit line", "Local bank", "Private bank", "Portfolio loan", "Foreign national loan", "Cash"] },
          { key: "mortgage_type", label: "Mortgage type", type: "select", options: ["Interest-only", "P&I (amortizing)", "Not sure"] },
          { key: "loan_product", label: "Loan product preference", type: "select", options: ["30-year fixed", "15-year fixed", "ARM", "I/O ARM", "Portfolio loan", "DSCR-style", "Not sure"] },
        ],
      },
      {
        title: "Income & Employment",
        tag: "calc",
        fields: [
          { key: "income_type", label: "Income type", type: "multiselect", required: true, options: ["W-2", "Bonus", "Commission", "1099", "Self-employed", "Business owner", "Distributions", "Investment income", "Rental income", "Other"] },
          { key: "annual_base_income", label: "Annual base income", type: "currency", required: true },
          { key: "bonus_commission_income", label: "Bonus / commission income", type: "currency", required: true },
          { key: "rental_other_income", label: "Rental / investment / other income", type: "currency", required: true },
          { key: "employer", label: "Employer / company", type: "text", required: true },
          { key: "income_currency", label: "Income currency", type: "select", options: ["USD", "EUR", "GBP", "Other"] },
          { key: "paid_in_usd", label: "Paid in USD?", type: "select", options: ["Yes", "No", "Partially", "Not sure"] },
          { key: "employer_tenure", label: "Length with employer / company", type: "text", placeholder: "e.g. 4 years" },
          { key: "self_employed_status", label: "Self-employed / business owner", type: "select", options: ["Yes", "No", "Partially", "Not sure"] },
          { key: "docs_available", label: "Documentation available", type: "multiselect", options: ["Tax returns", "W-2", "Paystubs", "Bank statements", "Brokerage statements", "CPA letter", "PFS"] },
        ],
      },
      {
        title: "Banking Relationships",
        tag: "calc",
        fields: [
          { key: "current_banks", label: "Current banking relationships", type: "text", required: true },
          { key: "private_bank_relationship", label: "Private bank relationship", type: "select", options: ["Yes", "No", "Potentially", "Not sure"] },
          { key: "relationship_pricing_assets", label: "Relationship-pricing assets available", type: "currency", help: "Assets the client may move to a bank" },
        ],
      },
      {
        title: "Pre-approval",
        tag: "calc",
        fields: [
          { key: "preapproval_status", label: "Pre-approval status", type: "select", options: ["Yes", "No", "In process", "Cash buyer"] },
          { key: "preapproval_bank", label: "Pre-approval bank", type: "text" },
          { key: "preapproval_amount", label: "Pre-approval amount", type: "currency", help: "Does not automatically equal target budget" },
          { key: "preapproval_expiration", label: "Pre-approval expiration", type: "date" },
        ],
      },
      {
        title: "Entity / Closing",
        tag: "crm",
        fields: [
          { key: "llc_requirement", label: "LLC requirement", type: "select", options: ["Must", "Prefer", "No", "Not sure"] },
          { key: "trust_requirement", label: "Trust requirement", type: "select", options: ["Yes", "No", "Prefer", "Not sure"] },
        ],
      },
      {
        title: "System Estimates",
        tag: "calc",
        note: "Computed from the profile above — read-only.",
        fields: [
          { key: "est_interest_rate", label: "Estimated interest rate", type: "computed" },
          { key: "rate_quote_date", label: "Rate quote date", type: "computed" },
          { key: "rate_confidence", label: "Rate confidence", type: "computed" },
          { key: "reserves_required", label: "Estimated reserves required", type: "computed" },
          { key: "max_loan_amount", label: "Estimated max loan amount", type: "computed" },
          { key: "max_qualified_price", label: "Estimated max qualified purchase price", type: "computed" },
          { key: "all_in_monthly_payment", label: "Estimated all-in monthly payment", type: "computed" },
          { key: "rent_to_cover_debt_service", label: "Rent needed to cover debt service", type: "computed" },
          { key: "financing_data_confidence", label: "Financing-data confidence", type: "computed" },
        ],
      },
    ],
  },

  // ───────────────────────────── SECTION 2 ─────────────────────────────
  {
    id: "search",
    label: "Search Parameters",
    title: "Search Parameters",
    subtitle: "What the client wants. Almost every field here maps onto a listing attribute — this is the matching engine's input.",
    boxes: [
      {
        title: "Use & Horizon",
        tag: "match",
        fields: [
          { key: "intended_use", label: "Intended use", type: "select", required: true, options: ["Residential", "Mostly residential", "Both", "Investment", "Portfolio investor"] },
          { key: "primary_focus", label: "Primary focus for purchase", type: "select", required: true, options: ["Cap rate", "Appreciation", "Both", "Lifestyle", "Not sure"] },
          { key: "hold_period", label: "Expected hold period", type: "select", options: ["Short term (1–5 yrs)", "Medium (5–10 yrs)", "Long (10+ yrs)", "Unknown"] },
          { key: "resale_importance", label: "Liquidity / resale importance", type: "select", options: ["High", "Medium", "Low"] },
        ],
      },
      {
        title: "Price",
        tag: "match",
        fields: [
          { key: "max_purchase_price", label: "Maximum purchase price", type: "currency", required: true },
          { key: "min_purchase_price", label: "Minimum purchase price", type: "currency" },
          { key: "stretch_purchase_price", label: "Stretch purchase price", type: "currency", help: "Considered for exceptional fit — penalty, not reject" },
          { key: "budget_stickiness", label: "Budget stickiness", type: "select", options: ["Strict", "Moderate", "Flexible"] },
        ],
      },
      {
        title: "Cash Flow & Returns",
        tag: "match",
        fields: [
          { key: "required_cash_flow", label: "Required cash flow", type: "select", required: true, options: ["Positive", "Break-even", "Negative allowed", "Minimum $ amount"] },
          { key: "required_cash_flow_amount", label: "Minimum monthly cash flow ($)", type: "currency" },
          { key: "max_carrying_cost", label: "Max monthly carrying cost", type: "currency", help: "Taxes + common charges + mortgage" },
          { key: "required_cap_rate", label: "Required cap rate", type: "percent" },
          { key: "target_cap_rate", label: "Target cap rate", type: "percent" },
          { key: "buffer_from_breakeven", label: "Buffer from break-even", type: "text", placeholder: "$ or %" },
          { key: "min_estimated_rent", label: "Minimum estimated rent", type: "currency" },
          { key: "risk_tolerance", label: "Risk tolerance", type: "select", options: ["Risk averse", "Medium", "Opportunistic", "Any ok"] },
          { key: "tax_abatement_tolerance", label: "Tax abatement tolerance", type: "select", options: ["Wants abatement", "Accepts phase-in", "Avoids phase-in", "Not relevant"] },
        ],
      },
      {
        title: "Location",
        tag: "match",
        fields: [
          { key: "boroughs", label: "Borough preferences", type: "boroughs", required: true },
          { key: "preferred_neighborhood_ids", label: "Preferred neighborhoods", type: "neighborhoods", required: true },
          { key: "acceptable_neighborhood_ids", label: "Acceptable neighborhoods", type: "neighborhoods" },
          { key: "excluded_neighborhood_ids", label: "Excluded neighborhoods", type: "neighborhoods", help: "Usually a hard reject" },
          { key: "preferred_building_ids", label: "Preferred buildings", type: "buildings", help: "Search our buildings — stored & matched by property (high score if matched)" },
          { key: "excluded_building_ids", label: "Excluded buildings", type: "buildings", accent: "danger", help: "Search our buildings — hard reject if matched" },
          { key: "key_locations", label: "Key locations", type: "keylocations", help: "Match listings within a radius of one or more places (office, school, family). All are required (intersect)." },
        ],
      },
      {
        title: "Property Type & Ownership",
        tag: "match",
        fields: [
          // Values are ownership_type codes so the matcher maps directly (TODO3/#1).
          { key: "property_type", label: "Property type preference", type: "multiselect", required: true, options: [
            { value: "CONDO", label: "Condo" },
            { value: "CONDP", label: "Condop" },
            { value: "COOP", label: "Co-op" },
            { value: "TOWNHOUSE", label: "Townhouse" },
            { value: "MULTI", label: "Multifamily" },
          ] },
          // New development is a separate preference (not an ownership type): a
          // three-way require/exclude/ignore filter. "Must not have" == resale only.
          // Must-have auto-checks "New development" in the Building character field below.
          { key: "new_development", label: "New development", type: "select", required: true,
            options: [
              { value: "MUST_HAVE", label: "Must have" },
              { value: "EXCLUDE", label: "Must not have" },
              { value: "NOT_RELEVANT", label: "Not relevant" },
            ],
            effects: [{ when: "MUST_HAVE", addTo: "building_character_pref", value: "new_dev" }] },
          { key: "investment_coops", label: "Investment co-ops", type: "select", required: true, options: ["Yes", "No", "Not sure"], help: "Will they consider investor-friendly co-ops?" },
        ],
      },
      {
        title: "Unit Specs",
        tag: "match",
        fields: [
          { key: "bedroom_min", label: "Bedroom minimum", type: "select", options: ["Studio", "1", "2", "3", "3+"] },
          { key: "bedroom_max", label: "Bedroom maximum", type: "select", options: ["Studio", "1", "2", "3", "3+", "No max"] },
          { key: "bathroom_min", label: "Bathroom minimum", type: "select", options: ["1", "1.5", "2", "2.5", "3+"] },
          { key: "sqft_min", label: "SQFT minimum", type: "number", placeholder: "Listings missing SF are still included" },
          { key: "floor_preference", label: "Floor preference", type: "select", options: ["Ground ok", "Low floor", "Mid floor", "High floor", "No preference"] },
          { key: "ground_floor_acceptable", label: "Ground floor acceptable?", type: "select", options: ["Yes", "No", "Maybe", "Not relevant"] },
          { key: "outdoor_space", label: "Outdoor space", type: "select", options: ["Must-have", "Preferred", "Not relevant"] },
        ],
      },
      {
        title: "Features & Building",
        tag: "match",
        fields: [
          // Selecting an intensity auto-adds "Doorman" to the matching feature list below
          // (additive convenience — rep can still deselect). See setField effects handling.
          { key: "doorman_building_pref", label: "Doorman building preference", type: "select", required: true,
            options: ["Must-have", "Preferred", "Not relevant"],
            effects: [
              { when: "Must-have", addTo: "must_have_features", value: "Doorman" },
              { when: "Preferred", addTo: "nice_to_have_features", value: "Doorman" },
            ] },
          { key: "must_have_features", label: "Must-have features", type: "multiselect", options: FEATURE_OPTIONS, help: "Hard reject if missing" },
          { key: "nice_to_have_features", label: "Nice-to-have features", type: "multiselect", options: FEATURE_OPTIONS },
          { key: "deal_breakers", label: "Deal-breakers", type: "multiselect", options: ["Walk-up", "Doorman", "No laundry", "High monthlies", "Ground floor", "Tenant occupied", "Co-op", "Needs renovation"] },
          { key: "general_preferences", label: "General asset preferences", type: "multiselect", options: FEATURE_OPTIONS },
          // Values are exact realplus_properties.building_tier strings (building_classification.py)
          // so the matcher maps 1:1. "Luxury" is manual-only but a valid classification.
          { key: "building_type_pref", label: "Building type preference", type: "multiselect", options: [
            { value: "Walk up", label: "Walk-up (no elevator)" },
            { value: "Walk up plus", label: "Walk-up + amenities" },
            { value: "E only", label: "Elevator, no doorman" },
            { value: "E plus", label: "Elevator + amenities, no doorman" },
            { value: "E/D only", label: "Elevator + doorman" },
            { value: "Low", label: "Full-service (basic amenities)" },
            { value: "Med", label: "Full-service (mid amenities)" },
            { value: "High", label: "Full-service (full amenities)" },
            { value: "Luxury", label: "Luxury" },
          ] },
          // Era (from year_built) + status flags. "new_dev" is auto-checked from the
          // dedicated New development select (Property Type box) via its `effects` rule.
          { key: "building_character_pref", label: "Building character", type: "multiselect",
            help: "New construction = age (built 2000+); New development = sale status",
            options: [
              { value: "prewar", label: "Pre-war (before 1945)" },
              { value: "postwar", label: "Post-war (1945–2000)" },
              { value: "new_construction", label: "New construction (2000+)" },
              { value: "tax_abated", label: "Tax-abated" },
              { value: "new_dev", label: "New development" },
            ] },
          { key: "amenity_preference", label: "Amenity preference", type: "multiselect", options: ["Gym", "Pool", "Roof deck", "Storage", "Garage", "Children's room", "Package room", "Live-in super"] },
          // Simplified: we only reliably store a pets-allowed boolean on listings.
          // Clearable (empty = no preference).
          { key: "pet_requirement", label: "Pets", type: "select", options: [
            { value: "allowed", label: "Pets allowed" },
            { value: "not_allowed", label: "Pets not allowed" },
          ] },
        ],
      },
      {
        title: "Risk & Condition",
        tag: "match",
        fields: [
          { key: "willing_to_renovate", label: "Willing to renovate", type: "select", options: ["Yes", "Yes up to $X", "No", "Not relevant"] },
          { key: "renovation_budget", label: "Renovation budget", type: "currency" },
          // Values are ai_renovation levels (1-5, higher = more renovated) for a 1:1 matcher map.
          { key: "renovation_tolerance", label: "Renovation level tolerance", type: "select", options: [
            { value: "5", label: "Turnkey" },
            { value: "4", label: "Light cosmetic" },
            { value: "3", label: "Moderate" },
            { value: "2", label: "Kitchen-bath" },
            { value: "1", label: "Full / gut renovation" },
          ] },
        ],
      },
      {
        // Fields we can't reliably auto-match on (data is sparse or unstructured). These are
        // NOT used by the matcher — `match: "review"` flags them for the rep to assess per
        // listing on the results page. Kept here so the investor's stated preference is captured.
        title: "Need to know — manual review",
        tag: "review",
        note: "We don't have reliable data to auto-match these. They're captured as preferences and reviewed per listing by hand.",
        fields: [
          { key: "view_preference", label: "View preference", type: "multiselect", match: "review", options: ["Open city", "Park", "Water", "Skyline", "Garden", "Not relevant"] },
          { key: "dark_unit", label: "Dark unit", type: "select", match: "review", options: ["Avoid", "Acceptable", "Not relevant"] },
          { key: "coop_sublet_flexibility", label: "Co-op sublet flexibility required", type: "select", match: "review", options: [
            { value: "allowed", label: "Unlimited" },
            { value: "vesting_period", label: "After 1 year" },
            { value: "sometimes", label: "Case-by-case" },
            { value: "NOT_RELEVANT", label: "Not relevant" },
          ] },
          { key: "tenant_occupied", label: "Tenant occupied acceptable?", type: "select", match: "review", options: ["Yes", "No", "Agnostic", "Only if market rent"] },
          { key: "delivered_vacant", label: "Delivered vacant required?", type: "select", match: "review", options: ["Yes", "No", "Maybe", "Not relevant"] },
          { key: "tenant_risk_tolerance", label: "Tenant risk tolerance", type: "select", match: "review", options: ["Low", "Medium", "High", "Not relevant"] },
          { key: "assessment_litigation_tolerance", label: "Assessment / litigation tolerance", type: "select", match: "review", options: ["None", "Minor ok", "Case-by-case", "Opportunistic"] },
        ],
      },
      {
        title: "Constraints & Confidence",
        tag: "calc",
        fields: [
          { key: "financing_compat_constraints", label: "Financing compatibility constraints", type: "computed", help: "Derived from the financing profile" },
          { key: "search_data_confidence", label: "Data confidence", type: "select", options: ["High", "Medium", "Low", "Manual review"] },
        ],
      },
    ],
  },

  // ───────────────────────────── SECTION 3 ─────────────────────────────
  {
    id: "internal",
    label: "Internal Rankings",
    title: "Internal Rankings (CRM)",
    subtitle: "How the team acts on the client — governs alerting and prioritization, not property fit.",
    boxes: [
      {
        title: "Engagement & Status",
        tag: "crm",
        fields: [
          { key: "client_engagement", label: "Client engagement", type: "select", required: true, options: ["Not interested", "Dormant", "Hold", "Push", "Active search", "Search", "Not yet defined", "Distribution list"] },
          { key: "priority_level", label: "Priority level", type: "select", required: true, options: ["High", "Medium", "Low", "Dormant"] },
          { key: "deal_status", label: "Client deal status", type: "select", options: ["No deal", "Offer sent", "Accepted offer", "In contract — no financing", "In contract — financing", "Closing scheduled", "Post-closing arrangements"] },
          { key: "buyer_readiness", label: "Buyer readiness", type: "select", options: ["Ready to offer", "Actively touring", "Monitoring", "Browsing", "Dormant"] },
          { key: "financing_readiness", label: "Financing readiness", type: "select", options: ["Cash verified", "Pre-approved", "Pre-approval pending", "Unknown", "Financing issue"], help: "Auto-suggested from Section 1 — override if needed" },
          { key: "strategy_segment", label: "Strategy / segment", type: "select", options: ["Yield investor", "Appreciation investor", "Luxury end-user", "First-time buyer", "1031 exchange", "Foreign investor", "Pied-à-terre", "Other"], help: "Auto-suggested from intended use & focus — override if needed" },
          { key: "search_clarity", label: "Search clarity", type: "select", options: ["Very clear", "Somewhat clear", "Vague", "Unknown"] },
        ],
      },
      {
        title: "Timeline & Communication",
        tag: "crm",
        fields: [
          { key: "timeline_to_buy", label: "Purchase timeline / urgency", type: "select", required: true, options: ["Immediate", "30 days", "60 days", "90 days", "6 months", "Flexible", "Browsing", "Unknown"], help: "Drives both CRM priority and how fast the client can close" },
          { key: "communication_preference", label: "Communication preference", type: "multiselect", required: true, options: ["WhatsApp", "Text", "Call", "Email"] },
          { key: "communication_frequency", label: "Communication frequency", type: "select", options: ["Weekly", "Biweekly", "Monthly", "Quarterly", "Every 6 months", "Yearly", "Do not contact"] },
          { key: "last_contact_date", label: "Last contact date", type: "date" },
          { key: "next_followup_date", label: "Next follow-up date", type: "date" },
        ],
      },
      {
        title: "Ownership & Relationship",
        tag: "crm",
        fields: [
          { key: "assigned_team_member_id", label: "Assigned team member", type: "users", required: true },
          { key: "relationship_value", label: "Relationship value", type: "select", required: true, options: ["Existing client", "Repeat investor", "Property management client", "Referral", "New lead", "Low priority"] },
          { key: "referral_source", label: "Referral source", type: "text", required: true, placeholder: "Client name, broker, website, event, organic…" },
          { key: "referred_by_similar_to", label: "Referred by / looking similar to", type: "text", help: "Client whose successful search this buyer resembles" },
        ],
      },
      {
        title: "Alerts",
        tag: "crm",
        fields: [
          { key: "alert_mode", label: "Alert mode", type: "select", required: true, options: ["Immediate", "Daily digest", "Weekly digest", "Manual review only", "Suppressed"] },
          { key: "alert_threshold", label: "Alert threshold", type: "number", placeholder: "60–100 match score" },
          { key: "match_appetite", label: "Match appetite", type: "select", options: ["Only exact matches", "Strong matches", "Send interesting near-misses", "Broad market updates"] },
          { key: "do_not_contact", label: "Do-not-contact / suppression", type: "select", options: ["No", "Temporarily suppress", "Do not contact"] },
        ],
      },
      {
        title: "Activity History",
        tag: "crm",
        fields: [
          { key: "last_property_sent", label: "Last property sent", type: "text", placeholder: "Address or listing ID" },
          { key: "last_showing_date", label: "Last showing date", type: "date" },
          { key: "showing_activity_level", label: "Showing activity level", type: "select", options: ["High", "Medium", "Low", "None"] },
          { key: "offer_history", label: "Offer history", type: "select", options: ["None", "1 offer", "Multiple offers", "Accepted offer"] },
          { key: "current_objections", label: "Current objections", type: "multiselect", options: ["Price", "Rates", "Timing", "Neighborhood", "Uncertainty", "Inventory", "Liquidity", "Taxes", "Renovation"] },
        ],
      },
      {
        title: "Data Quality",
        tag: "crm",
        fields: [
          { key: "criteria_confidence", label: "Criteria confidence", type: "select", options: ["High", "Medium", "Low", "Stale"] },
          { key: "data_freshness", label: "Data freshness", type: "date", help: "Last date criteria were confirmed" },
          { key: "internal_notes", label: "Internal notes", type: "textarea" },
        ],
      },
    ],
  },
];

/** Flat list of every field key, useful for building empty form state. */
export const ALL_FIELD_KEYS = ONBOARDING_SCHEMA.flatMap((s) =>
  s.boxes.flatMap((b) => b.fields.map((f) => f.key))
);
